import { FC, useEffect, useState } from "react";
//#region redux
import { useDispatch } from "react-redux";
import { displayDialog } from "store/actions/utilActions";
//#endregion
//#region types
import { WintSuperkollAPIModelWintSalaryReport } from "wint-api-client/dist/types";
import { SalaryDeviationFormTypes } from "models/formTypes";
//#endregion
//#region components
import WintSalaryReportTable from "./wintSalaryReportTable";
import { useFilterContext, useSearchContext } from "components/common";
import useWintDeviationCreateDialog from "../useWintDeviationCreateDialog";
//#endregion
import { useWintSalaryDeviationService } from "serviceHooks";
import useDeviationPermissions from "components/salaryDeviation/usePermissions";

const DeviationReportInnerView: FC<{ reloadKey: number; reloadHandler: () => void }> = ({
  reloadKey,
  reloadHandler,
}) => {
  const dispatch = useDispatch();

  const [dataRows, setDataRows] = useState<WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow[] | undefined>();
  const [loading, setLoading] = useState(false);

  const searchContext = useSearchContext();

  const { getDeviationReport, isDeviationStateLocked } = useWintSalaryDeviationService();
  const createWintDeviationDialog = useWintDeviationCreateDialog();

  const { canManageSalary } = useDeviationPermissions();

  const { filter } = useFilterContext<SalaryDeviationFormTypes.WintSalaryDeviationReportFilter>();

  // A row that cannot be edited is still opened — read-only — so the details stay viewable.
  // The dialog locks per month via isYearMonthLocked; this decides whether the deviation is
  // editable *at all*. Both go through isDeviationStateLocked so the two cannot disagree.
  const isRowEditable = (column?: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRowColumn) =>
    !!column && !isDeviationStateLocked(column.state, column.isSalaryMonthFinalized, canManageSalary);

  const onRowClick = (
    row: WintSuperkollAPIModelWintSalaryReport.WintSalaryReportRow,
    rowIndex: number,
    depth?: number
  ) => {
    const column = row.columns?.[0];
    if (depth !== 2 || !column?.connectedDeviationId) return;

    dispatch(
      displayDialog(
        createWintDeviationDialog(
          column.connectedDeviationId,
          undefined,
          column.connectedDeviationTypeId,
          reloadHandler,
          filter?.yearMonthRange,
          !isRowEditable(column)
        )
      )
    );
  };

  useEffect(() => {
    fetchData();
  }, [filter, searchContext?.searchText, reloadKey]);

  const fetchData = async () => {
    if (!filter) return;
    const { selectedFilter, ...filterFormFields } = filter;
    setLoading(true);
    try {
      const dataRows = await getDeviationReport({
        ...filterFormFields,
        employeeSearchName: searchContext?.searchText,
      } as SalaryDeviationFormTypes.WintSalaryDeviationReportFilter);
      setDataRows(dataRows);
    } catch (error) {
      // Handle error if needed
    } finally {
      setLoading(false);
    }
  };

  return (
    <WintSalaryReportTable
      rows={dataRows}
      hideUserPayoutDetails
      loading={loading}
      hiddenColumns={["amount"]}
      onRowClick={onRowClick}
      reloadHandler={reloadHandler}
    />
  );
};

export default DeviationReportInnerView;
